import { useState } from "react";
import { ThumbsUp, ThumbsDown, MinusCircle, Loader2, Send, BookOpen, CheckCircle2, X } from "lucide-react";
import { api } from "../lib/api";
import { useAria } from "../context/AriaContext";
import { toast } from "sonner";

/**
 * Three-tier ARIA feedback widget.
 *
 *   Tier 1  thumbs up / partial / down
 *   Tier 2  textarea for physician correction (revealed after partial/down)
 *   Tier 3  LLM evaluates correction → "updated" or "defended" with optional citations,
 *           ARIA reads the verdict aloud.
 *
 * Props:
 *   surface         : "voice_card" | "topology" | "huddle" | "module"
 *   originalQuery   : the physician question / context that produced the answer (optional)
 *   ariaResponse    : the text ARIA spoke / produced (required)
 *   resourceId      : e.g. topology analysis id, module slug (optional)
 *   compact         : tighter layout for the voice card
 *   onDone          : callback after verdict is read
 */
export default function AriaFeedback({
  surface = "voice_card",
  originalQuery = null,
  ariaResponse,
  resourceId = null,
  compact = false,
  onDone,
}) {
  const aria = useAria();
  const [rating, setRating] = useState(null);
  const [feedbackId, setFeedbackId] = useState(null);
  const [showCorrection, setShowCorrection] = useState(false);
  const [correction, setCorrection] = useState("");
  const [submittingRating, setSubmittingRating] = useState(false);
  const [submittingCorrection, setSubmittingCorrection] = useState(false);
  const [verdict, setVerdict] = useState(null);
  const [verdictText, setVerdictText] = useState("");
  const [citations, setCitations] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || !ariaResponse) return null;

  const submitRating = async (r) => {
    if (submittingRating) return;
    setSubmittingRating(true);
    setRating(r);
    try {
      const res = await api.post("/aria/feedback", {
        rating: r,
        surface,
        original_query: originalQuery,
        aria_response: ariaResponse,
        resource_id: resourceId,
      });
      setFeedbackId(res.data.id);
      if (r === "up") {
        toast.success("Thanks — recorded as helpful");
        setTimeout(() => setDismissed(true), 1500);
      } else {
        // Tier 2 reveal
        setShowCorrection(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record feedback");
      setRating(null);
    } finally {
      setSubmittingRating(false);
    }
  };

  const submitCorrection = async (e) => {
    e?.preventDefault?.();
    if (!feedbackId || !correction.trim() || submittingCorrection) return;
    setSubmittingCorrection(true);
    try {
      const res = await api.post(`/aria/feedback/${feedbackId}/correction`, {
        correction: correction.trim(),
      });
      setVerdict(res.data.verdict);
      setVerdictText(res.data.text);
      setCitations(res.data.citations || []);
      // ARIA speaks the verdict
      try { await aria.speak(res.data.text); } catch (_) {}
      onDone?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not evaluate correction");
    } finally {
      setSubmittingCorrection(false);
    }
  };

  // ── Render ──
  const wrapCls = compact
    ? "mt-3 pt-3 border-t border-[var(--pa-border)]"
    : "mt-4 p-3 rounded-md border border-[var(--pa-border)] bg-[var(--pa-bg-2)]";

  return (
    <div data-testid={`aria-feedback-${surface}`} className={wrapCls}>
      {/* Tier 1 — thumb buttons */}
      {rating === null && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] uppercase tracking-widest text-[var(--pa-text-dim)]">
            Was this helpful?
          </span>
          <div className="flex items-center gap-1.5">
            <FeedbackThumb
              testid="aria-feedback-up"
              ariaLabel="Helpful"
              icon={ThumbsUp}
              onClick={() => submitRating("up")}
              disabled={submittingRating}
              tone="emerald"
            />
            <FeedbackThumb
              testid="aria-feedback-partial"
              ariaLabel="Partially helpful"
              icon={MinusCircle}
              onClick={() => submitRating("partial")}
              disabled={submittingRating}
              tone="amber"
            />
            <FeedbackThumb
              testid="aria-feedback-down"
              ariaLabel="Not helpful"
              icon={ThumbsDown}
              onClick={() => submitRating("down")}
              disabled={submittingRating}
              tone="red"
            />
          </div>
        </div>
      )}

      {/* Tier 2 — correction textarea */}
      {rating && rating !== "up" && showCorrection && !verdict && (
        <form onSubmit={submitCorrection} className="space-y-2" data-testid="aria-correction-form">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase tracking-widest text-amber-300">
              What should ARIA have said?
            </span>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              data-testid="aria-feedback-cancel"
              className="text-[var(--pa-text-dim)] hover:text-[var(--pa-text)]"
              aria-label="Cancel feedback"
            >
              <X size={12} />
            </button>
          </div>
          <textarea
            value={correction}
            onChange={(e) => setCorrection(e.target.value)}
            placeholder="Briefly explain what ARIA missed or got wrong. ARIA will reconsider and respond."
            rows={3}
            maxLength={4000}
            data-testid="aria-correction-text"
            autoFocus
            className="w-full text-xs bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md p-2 text-[var(--pa-text)] focus:border-[var(--pa-gold)] focus:outline-none resize-y"
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] text-[var(--pa-text-dim)]">
              ARIA will evaluate and reply with either an updated answer or a cited defense.
            </span>
            <button
              type="submit"
              disabled={submittingCorrection || !correction.trim()}
              data-testid="aria-correction-submit"
              className="inline-flex items-center gap-1.5 text-xs bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded-md px-3 py-1.5 hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-50"
            >
              {submittingCorrection ? (
                <>
                  <Loader2 size={11} className="animate-spin" /> ARIA is evaluating…
                </>
              ) : (
                <>
                  <Send size={11} /> Send to ARIA
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Tier 3 — verdict */}
      {verdict && (
        <div
          data-testid={`aria-feedback-verdict-${verdict}`}
          className={`p-3 rounded-md border ${
            verdict === "updated"
              ? "border-emerald-500/40 bg-emerald-500/5"
              : "border-[var(--pa-gold)]/40 bg-[var(--pa-gold)]/5"
          }`}
        >
          <div className={`text-[10px] uppercase tracking-widest mb-1.5 flex items-center gap-1.5 ${
            verdict === "updated" ? "text-emerald-300" : "text-[var(--pa-gold)]"
          }`}>
            {verdict === "updated" ? <CheckCircle2 size={11} /> : <BookOpen size={11} />}
            {verdict === "updated" ? "ARIA accepted your correction" : "ARIA stands by her answer"}
          </div>
          <p className="text-xs text-[var(--pa-text)] leading-relaxed" data-testid="aria-verdict-text">
            {verdictText}
          </p>
          {verdict === "defended" && citations.length > 0 && (
            <ul className="mt-3 space-y-1" data-testid="aria-verdict-citations">
              {citations.map((c, i) => (
                <li
                  key={i}
                  className="text-[10px] text-[var(--pa-text-muted)] leading-snug border-l border-[var(--pa-gold)]/40 pl-2"
                  data-testid={`aria-verdict-citation-${i}`}
                >
                  <span className="text-[var(--pa-gold)]">{c.label || `Ref ${i + 1}`}</span>
                  {c.title ? ` — ${c.title}` : ""}
                  {c.source ? ` · ${c.source}` : ""}
                </li>
              ))}
            </ul>
          )}
          <button
            onClick={() => setDismissed(true)}
            data-testid="aria-verdict-dismiss"
            className="mt-2 text-[10px] text-[var(--pa-text-dim)] hover:text-[var(--pa-gold)] underline underline-offset-2"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}

function FeedbackThumb({ testid, ariaLabel, icon: Icon, onClick, disabled, tone }) {
  const toneCls = {
    emerald: "hover:bg-emerald-500/10 hover:text-emerald-300",
    amber: "hover:bg-amber-500/10 hover:text-amber-300",
    red: "hover:bg-red-500/10 hover:text-red-300",
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      data-testid={testid}
      className={`p-1.5 rounded-md text-[var(--pa-text-muted)] transition disabled:opacity-50 ${toneCls}`}
    >
      <Icon size={14} />
    </button>
  );
}
